import React, { useState, useEffect } from 'react';
import { Shield, Key, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../UI';
import TwoFactorAuth from './TwoFactorAuth';
import { totpService } from '../../services/totpService';
import { useAuth } from '../../contexts/AuthContext';
import './TwoFactorSettings.css';

/**
 * 설정 페이지용 2단계 인증 관리 컴포넌트
 */
function TwoFactorSettings() {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTwoFactorStatus();
  }, [user]);

  const loadTwoFactorStatus = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const settings = await totpService.getTwoFactorSettings(user.uid);
      setIsEnabled(settings?.enabled || false);
      setBackupCodes(settings?.backupCodes || []);
    } catch (error) {
      console.error('2FA 상태 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = (settings) => {
    setIsEnabled(true);
    setBackupCodes(settings.backupCodes);
    setShowSetupModal(false);
  };

  const handleDisable2FA = async () => {
    if (window.confirm('2단계 인증을 비활성화하시겠습니까?\n\n보안이 약해질 수 있습니다.')) {
      try {
        await totpService.disableTwoFactor(user.uid);
        setIsEnabled(false);
        setBackupCodes([]);
        alert('2단계 인증이 비활성화되었습니다.');
      } catch (error) {
        console.error('2FA 비활성화 실패:', error);
        alert('2단계 인증 비활성화 중 오류가 발생했습니다.');
      }
    }
  };

  const handleDownloadBackupCodes = () => {
    const content = [
      '# NewPass2 백업 코드',
      '# 안전한 곳에 보관하세요',
      '# 각 코드는 한 번만 사용할 수 있습니다',
      '',
      ...backupCodes.map((code, index) => `${index + 1}. ${code}`)
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newpass2-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const regenerateBackupCodes = async () => {
    if (window.confirm('새로운 백업 코드를 생성하시겠습니까?\n\n기존 백업 코드는 모두 무효화됩니다.')) {
      try {
        const newCodes = totpService.generateBackupCodes();
        await totpService.updateBackupCodes(user.uid, newCodes);
        setBackupCodes(newCodes);
        alert('새로운 백업 코드가 생성되었습니다.\n안전한 곳에 저장해주세요.');
      } catch (error) {
        console.error('백업 코드 재생성 실패:', error);
        alert('백업 코드 재생성 중 오류가 발생했습니다.');
      }
    }
  };

  if (loading) {
    return (
      <div className="two-factor-settings loading">
        <div className="loading-spinner"></div>
        <span>2단계 인증 상태 확인 중...</span>
      </div>
    );
  }

  return (
    <div className="two-factor-settings">
      <div className="two-factor-status">
        <div className="status-header">
          <div className="status-icon">
            {isEnabled ? (
              <CheckCircle size={24} className="status-enabled" />
            ) : (
              <AlertCircle size={24} className="status-disabled" />
            )}
          </div>
          <div className="status-info">
            <h3>{isEnabled ? '2단계 인증 활성화됨' : '2단계 인증 비활성화됨'}</h3>
            <p>
              {isEnabled 
                ? '계정이 추가 보안으로 보호되고 있습니다.'
                : '계정 보안을 위해 2단계 인증을 활성화하는 것이 좋습니다.'
              }
            </p>
          </div>
        </div>

        <div className="status-actions">
          {!isEnabled ? (
            <Button
              variant="primary"
              onClick={() => setShowSetupModal(true)}
            >
              <Shield size={16} />
              2단계 인증 설정
            </Button>
          ) : (
            <div className="enabled-actions">
              <Button
                variant="secondary"
                onClick={regenerateBackupCodes}
              >
                <Key size={16} />
                백업 코드 재생성
              </Button>
              <Button
                variant="danger"
                onClick={handleDisable2FA}
              >
                비활성화
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 2단계 인증이 활성화된 경우 백업 코드 관리 */}
      {isEnabled && backupCodes.length > 0 && (
        <div className="backup-codes-section">
          <h4>
            <Key size={18} />
            백업 코드 관리
          </h4>
          <p>
            인증 앱에 접근할 수 없을 때 사용할 수 있는 일회용 백업 코드입니다.
            안전한 곳에 보관하세요.
          </p>

          <div className="backup-codes-grid">
            {backupCodes.map((code, index) => (
              <div key={index} className="backup-code">
                <span className="code-number">{index + 1}</span>
                <code>{code}</code>
              </div>
            ))}
          </div>

          <div className="backup-actions">
            <Button
              variant="secondary"
              onClick={handleDownloadBackupCodes}
            >
              <Download size={16} />
              백업 코드 다운로드
            </Button>
          </div>

          <div className="backup-warning">
            <AlertCircle size={16} />
            <span>각 백업 코드는 한 번만 사용할 수 있습니다. 사용한 코드는 무효화됩니다.</span>
          </div>
        </div>
      )}

      {/* 2단계 인증 설정 모달 */}
      {showSetupModal && (
        <TwoFactorAuth
          isOpen={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          onSetupComplete={handleSetupComplete}
        />
      )}
    </div>
  );
}

export default TwoFactorSettings;